import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

interface BusinessInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  description: string;
  businessHours: string;
}

interface SettingsContextType {
  businessInfo: BusinessInfo | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

interface SettingsProviderProps {
  children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPublicSettings();
      
      if (response.status === 'success' && response.data?.business) {
        setBusinessInfo(response.data.business);
      } else {
        throw new Error('Failed to load business settings');
      }
    } catch (err) {
      console.error('Error loading business settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to load business settings');
      
      // Set fallback business info
      setBusinessInfo({
        name: 'Gandi Mall',
        address: '33 Magamba Way, Chinhoyi',
        phone: '+263 XX XXX XXXX',
        email: 'info@gandimall.com',
        description: 'Premium shopping destination in the heart of the city',
        businessHours: '9:00 AM - 10:00 PM'
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshSettings = async () => {
    await loadSettings();
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const value: SettingsContextType = {
    businessInfo,
    loading,
    error,
    refreshSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
